'use client'

/*
 * DigitSpanTest
 *
 * Digit span, the working-memory subtest from the Wechsler scales, run in the
 * browser.
 *
 * WHAT THE RESEARCH FIXES, AND WHAT IS A PRODUCT DECISION:
 *
 * Fixed by the clinical paradigm:
 *   - Digits are presented one per second. That rate is part of the task; show
 *     them faster and you are measuring reading speed instead.
 *   - Forward and backward are different tests. Forward is mostly short-term
 *     storage; backward makes you hold the sequence AND operate on it, which is
 *     why it runs one to two digits shorter and correlates better with g.
 *   - No digit repeats immediately, and sequences avoid runs like 3-4-5, both
 *     of which would let someone chunk their way past the span limit.
 *
 * Product decisions, made deliberately:
 *   - ONE trial per span length, not the WAIS-IV's two-with-discontinue. Two
 *     trials is better measurement and worse to sit through. The result screen
 *     says so rather than implying clinical rigour we did not earn.
 *   - Start at three digits. Nobody fails three, and an easy first round is
 *     what gets people to a second round.
 *
 * WHY THIS REPORTS A PERCENTILE:
 * Every other browser digit span tells you "your span is 7" and stops, which
 * is meaningless without knowing what 7 is worth. Percentile is the thing this
 * site already exists to explain, so the result is scored against published
 * adult norms and reuses the same normalCDF the IQ calculator uses. It is an
 * estimate from a normal approximation over a discrete score, and the result
 * screen says that too.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import Link from 'next/link'
import { Play, RotateCcw, ArrowRight, Check, X } from 'lucide-react'
import { normalCDF } from '@/lib/iqStats'
import TestPromptPopup from '@/components/TestPromptPopup'

// Published adult norms for maximum span. Forward sits near 7 (the "magical
// number seven" finding), backward one to two digits below it.
const NORMS = {
  forward: { mean: 6.9, sd: 1.3, label: 'Forward' },
  backward: { mean: 5.0, sd: 1.3, label: 'Backward' },
}

const START_SPAN = 3
const MAX_SPAN = 12
const DIGIT_MS = 1000   // one digit per second, per the clinical paradigm
const BLANK_MS = 300    // gap so repeated positions read as separate digits

// Avoids immediate repeats and simple runs, which are chunkable and would let
// someone beat their real span by spotting a pattern rather than holding it.
function generateSequence(length) {
  const seq = []
  while (seq.length < length) {
    const d = Math.floor(Math.random() * 10)
    const prev = seq[seq.length - 1]
    const prev2 = seq[seq.length - 2]
    if (d === prev) continue
    if (prev !== undefined && prev2 !== undefined) {
      const ascending = d === prev + 1 && prev === prev2 + 1
      const descending = d === prev - 1 && prev === prev2 - 1
      if (ascending || descending) continue
    }
    seq.push(d)
  }
  return seq
}

function percentileForSpan(span, mode) {
  const { mean, sd } = NORMS[mode]
  // Percentile rank of a discrete score is conventionally the share scoring
  // below it plus half the share scoring exactly it, which for a normal
  // approximation reduces to plain (span - mean) / sd with no continuity
  // correction. Subtracting 0.5 here, as an earlier version did, scores a span
  // of 7 at the 38th percentile when the adult forward average is 6.9 and it
  // should sit just above the 50th.
  return normalCDF((span - mean) / sd) * 100
}

// Normal curve drawn in z-space, so one path serves both modes and only the
// marker moves. Same shape as the bell curve on the calculator, at strip size.
const CURVE = (() => {
  const pts = []
  for (let i = 0; i <= 80; i++) {
    const z = -3 + (i / 80) * 6
    const x = 8 + ((z + 3) / 6) * 284
    const y = 62 - Math.exp(-(z * z) / 2) * 48
    pts.push(`${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`)
  }
  return pts.join(' ')
})()

function zToX(z) {
  const c = Math.max(-3, Math.min(3, z))
  return 8 + ((c + 3) / 6) * 284
}

// How strongly each mode relates to general ability.
//
// Digit Span Forward correlates with a fluid intelligence latent variable at
// about .51 in the WAIS-IV normative sample (N = 2,200), with a bifactor g
// loading near .46. Backward is consistently the stronger correlate. The values
// below sit at the conservative end of those ranges, because the whole point of
// what this feeds is the SIZE of the error band, and overstating r would shrink
// the band and overstate what one subtest can tell you.
const G_LOADING = { forward: 0.45, backward: 0.55 }

// Predicts full-scale IQ from a span, with the honest error bar around it.
//
// Regression to the mean does the work: a span two SDs above average predicts
// an IQ only r * 2 SDs above average, not 2. The standard error of estimate,
// 15 * sqrt(1 - r^2), is what makes the interval enormous, and the enormous
// interval is the actual finding. One subtest cannot tell you your IQ. It can
// only tell you which 50-point stretch of the scale you are probably in.
function iqEstimateFromSpan(span, mode) {
  const { mean, sd } = NORMS[mode]
  const r = G_LOADING[mode]
  const z = (span - mean) / sd
  const predicted = 100 + r * z * 15
  const margin = 1.959963984540054 * 15 * Math.sqrt(1 - r * r)
  return {
    predicted,
    low: Math.max(40, predicted - margin),
    high: Math.min(160, predicted + margin),
    margin,
  }
}

function verdictForPercentile(pct) {
  if (pct >= 98) return { label: 'Exceptional', theme: 'text-purple-700 bg-purple-100' }
  if (pct >= 84) return { label: 'Well above average', theme: 'text-blue-700 bg-blue-100' }
  if (pct >= 60) return { label: 'Above average', theme: 'text-emerald-700 bg-emerald-100' }
  if (pct >= 40) return { label: 'Average', theme: 'text-slate-700 bg-slate-100' }
  if (pct >= 16) return { label: 'Below average', theme: 'text-amber-700 bg-amber-100' }
  return { label: 'Well below average', theme: 'text-orange-700 bg-orange-100' }
}

export default function DigitSpanTest() {
  const [mode, setMode] = useState('forward')
  const [phase, setPhase] = useState('idle') // idle | showing | input | feedback | finished
  const [sequence, setSequence] = useState([])
  const [shownIndex, setShownIndex] = useState(-1)
  const [entry, setEntry] = useState('')
  const [span, setSpan] = useState(START_SPAN)
  const [bestSpan, setBestSpan] = useState(0)
  const [lastCorrect, setLastCorrect] = useState(null)

  const [personalBest, setPersonalBest] = useState(null)
  const [isNewBest, setIsNewBest] = useState(false)
  const [countUp, setCountUp] = useState(0)

  const inputRef = useRef(null)
  const timers = useRef([])

  const clearTimers = () => {
    timers.current.forEach(clearTimeout)
    timers.current = []
  }
  useEffect(() => clearTimers, [])

  // Plays the sequence one digit at a time, then hands over to the input phase.
  const playSequence = useCallback((seq) => {
    clearTimers()
    setShownIndex(-1)
    setPhase('showing')

    seq.forEach((_, i) => {
      timers.current.push(setTimeout(() => setShownIndex(i), i * (DIGIT_MS + BLANK_MS)))
      timers.current.push(
        setTimeout(() => setShownIndex(-1), i * (DIGIT_MS + BLANK_MS) + DIGIT_MS),
      )
    })

    timers.current.push(
      setTimeout(() => {
        setPhase('input')
        setEntry('')
      }, seq.length * (DIGIT_MS + BLANK_MS)),
    )
  }, [])

  const startRound = useCallback((length) => {
    const seq = generateSequence(length)
    setSequence(seq)
    playSequence(seq)
  }, [playSequence])

  const start = () => {
    setSpan(START_SPAN)
    setBestSpan(0)
    setLastCorrect(null)
    startRound(START_SPAN)
  }

  const reset = () => {
    clearTimers()
    setPhase('idle')
    setSpan(START_SPAN)
    setBestSpan(0)
    setEntry('')
    setLastCorrect(null)
  }

  useEffect(() => {
    if (phase === 'input') inputRef.current?.focus()
  }, [phase])

  // Auto-submit as soon as the expected number of digits is entered. Making
  // someone reach for Enter after typing the last digit adds an action to every
  // single round, and the round count is the whole loop here.
  //
  // The short delay is deliberate: submitting on the same frame as the final
  // keystroke means the last digit is replaced by the feedback tick before the
  // eye registers it, which reads as the test eating the input. 250ms is long
  // enough to see what you typed and short enough not to feel like a wait.
  useEffect(() => {
    if (phase !== 'input') return
    if (sequence.length === 0 || entry.length !== sequence.length) return

    const t = setTimeout(() => submit(), 250)
    return () => clearTimeout(t)
    // submit is intentionally omitted: it is recreated every render, and
    // including it would restart the timer on each one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, phase, sequence.length])

  const submit = () => {
    if (phase !== 'input' || entry.length === 0) return

    const expected = mode === 'forward' ? sequence : [...sequence].reverse()
    const correct = entry === expected.join('')

    setLastCorrect(correct)
    setPhase('feedback')

    if (correct) {
      const newBest = sequence.length
      setBestSpan(newBest)
      if (newBest >= MAX_SPAN) {
        timers.current.push(setTimeout(() => setPhase('finished'), 900))
        return
      }
      timers.current.push(
        setTimeout(() => {
          setSpan(newBest + 1)
          startRound(newBest + 1)
        }, 900),
      )
    } else {
      timers.current.push(setTimeout(() => setPhase('finished'), 900))
    }
  }

  // Personal best per mode. localStorage rather than sessionStorage because a
  // score worth beating should survive closing the tab, and every accessor is
  // wrapped since private mode throws on access rather than returning null.
  const bestKey = `biqt_digit_span_best_${mode}`

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(bestKey)
      setPersonalBest(stored ? parseInt(stored, 10) : null)
    } catch {
      setPersonalBest(null)
    }
    setIsNewBest(false)
  }, [bestKey])

  // Counts the final number up rather than printing it. The number IS the
  // reward here, and a value that lands instantly is read and forgotten, while
  // one that climbs is watched. It also buys a beat before the rest of the
  // result appears.
  useEffect(() => {
    if (phase !== 'finished') { setCountUp(0); return }
    if (bestSpan === 0) return

    let frame
    const duration = 650
    const started = performance.now()

    const tick = (now) => {
      const t = Math.min(1, (now - started) / duration)
      // Ease-out: fast at first, settling on the final digit.
      const eased = 1 - Math.pow(1 - t, 3)
      setCountUp(Math.round(eased * bestSpan))
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [phase, bestSpan])

  useEffect(() => {
    if (phase !== 'finished' || bestSpan === 0) return
    if (personalBest !== null && bestSpan <= personalBest) return

    setIsNewBest(personalBest !== null)
    setPersonalBest(bestSpan)
    try {
      window.localStorage.setItem(bestKey, String(bestSpan))
    } catch {
      // Non-fatal. The best just will not persist for this visitor.
    }
  }, [phase, bestSpan, personalBest, bestKey])

  const percentile = bestSpan > 0 ? percentileForSpan(bestSpan, mode) : 0
  const estimate = iqEstimateFromSpan(bestSpan || NORMS[mode].mean, mode)
  const verdict = verdictForPercentile(percentile)
  const norm = NORMS[mode]

  return (
    <div className="bg-white border border-gray-200 rounded-3xl shadow-xl overflow-hidden">
      {/* Mode selector, locked once a run is underway so the norms stay valid */}
      <div className="px-6 sm:px-8 py-5 border-b border-gray-200 bg-gray-50/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="inline-flex rounded-xl bg-gray-200/80 p-1" role="group" aria-label="Test mode">
          {Object.entries(NORMS).map(([key, n]) => (
            <button
              key={key}
              type="button"
              disabled={phase !== 'idle' && phase !== 'finished'}
              onClick={() => { setMode(key); reset() }}
              aria-pressed={mode === key}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                mode === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {n.label}
            </button>
          ))}
        </div>
        <p className="text-sm text-gray-600">
          {mode === 'forward'
            ? 'Type the digits in the order you saw them.'
            : 'Type the digits in reverse order. Harder, and the better measure of working memory.'}
        </p>
      </div>

      {/* Stage */}
      <div className="px-6 sm:px-8 py-10 sm:py-14 min-h-[320px] flex flex-col items-center justify-center text-center">
        {phase === 'idle' && (
          <>
            <p className="text-gray-600 mb-2">
              Digits appear one at a time, one per second.
            </p>
            <p className="text-gray-500 text-sm mb-8 max-w-sm">
              Starts at {START_SPAN} digits and adds one each time you get it
              right. It ends on your first mistake.
            </p>
            <button
              type="button"
              onClick={start}
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold text-lg px-10 py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all"
            >
              <Play className="w-5 h-5 mr-2 fill-current" />
              Start
            </button>
            <Link
              href="/test"
              className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-blue-700 mt-5 transition-colors"
            >
              Or take the full 50-question IQ test
              <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Link>
          </>
        )}

        {phase === 'showing' && (
          <>
            <p className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-6">
              {sequence.length} digits
            </p>
            <div
              className="text-7xl sm:text-8xl font-bold text-gray-900 tabular-nums h-28 flex items-center"
              aria-live="off"
            >
              {shownIndex >= 0 ? sequence[shownIndex] : ''}
            </div>
            <div className="flex gap-1.5 mt-6" aria-hidden="true">
              {sequence.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 w-6 rounded-full transition-colors ${
                    i <= shownIndex ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {phase === 'input' && (
          <>
            <p className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-5">
              {mode === 'forward' ? 'Type them in order' : 'Type them backwards'}
            </p>
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={entry}
              onChange={(e) => setEntry(e.target.value.replace(/\D/g, '').slice(0, sequence.length))}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              aria-label="Enter the digits you saw"
              className="w-full max-w-xs text-center text-4xl sm:text-5xl font-bold tabular-nums tracking-[0.2em] text-gray-900 bg-white border-2 border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 rounded-2xl px-4 py-4 outline-none transition-all"
            />
            <div className="flex gap-1.5 mt-6" aria-hidden="true">
              {sequence.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 w-6 rounded-full transition-colors ${
                    i < entry.length ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-3">
              {entry.length} of {sequence.length}
            </p>
          </>
        )}

        {phase === 'feedback' && (
          <div className={`flex flex-col items-center ${lastCorrect ? 'text-emerald-600' : 'text-rose-600'}`}>
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${lastCorrect ? 'bg-emerald-100' : 'bg-rose-100'}`}>
              {lastCorrect ? <Check className="w-10 h-10" strokeWidth={3} /> : <X className="w-10 h-10" strokeWidth={3} />}
            </div>
            <p className="text-2xl font-bold">{lastCorrect ? 'Correct' : 'Not quite'}</p>
            {!lastCorrect && (
              <p className="text-gray-500 mt-2 tabular-nums">
                It was {(mode === 'forward' ? sequence : [...sequence].reverse()).join(' ')}
              </p>
            )}
          </div>
        )}

        {phase === 'finished' && (
          <div className="w-full -mx-6 sm:-mx-8 -my-10 sm:-my-14">
            {/* RESULT HERO.
                Light, in the same blue/indigo family as every other surface on
                the site. An earlier version made this panel dark for contrast,
                which gave the number presence but dropped a black block into
                the middle of a light page and read as a different site. The
                weight now comes from scale and the gradient numeral, not from
                inverting the palette. */}
            <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border-b border-indigo-100 px-6 sm:px-8 py-10 text-center">

              <div className="relative">
                {isNewBest && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold tracking-wide mb-4 shadow-sm">
                    <Check className="w-3.5 h-3.5 mr-1.5" strokeWidth={3} />
                    NEW PERSONAL BEST
                  </span>
                )}

                <p className="text-xs font-semibold text-indigo-500 uppercase tracking-[0.2em] mb-3">
                  {norm.label} digit span
                </p>

                <div className="text-8xl sm:text-9xl font-bold tabular-nums leading-none mb-4 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 bg-clip-text text-transparent">
                  {countUp}
                </div>

                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${verdict.theme}`}>
                    {verdict.label}
                  </span>
                  <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-white text-gray-700 border border-gray-200 tabular-nums shadow-sm">
                    {percentile < 1 ? '<1' : percentile > 99 ? '>99' : Math.round(percentile)}th percentile
                  </span>
                </div>

                <svg viewBox="0 0 300 78" className="w-full max-w-sm mx-auto mt-7" role="img" aria-label={`Your span sits at roughly the ${Math.round(percentile)}th percentile of adults`}>
                  <defs>
                    <linearGradient id="dsFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="#6366f1" stopOpacity="0.04" />
                    </linearGradient>
                  </defs>
                  <path d={`${CURVE} L 292 62 L 8 62 Z`} fill="url(#dsFill)" />
                  <path d={CURVE} fill="none" stroke="#6366f1" strokeWidth="1.5" />
                  <line x1="8" y1="62" x2="292" y2="62" stroke="#cbd5e1" strokeWidth="1" />
                  <line x1={zToX((bestSpan - norm.mean) / norm.sd)} y1="10" x2={zToX((bestSpan - norm.mean) / norm.sd)} y2="62" stroke="#7c3aed" strokeWidth="2" strokeDasharray="4,3" />
                  <circle cx={zToX((bestSpan - norm.mean) / norm.sd)} cy="62" r="4.5" fill="#7c3aed" />
                  <text x={zToX((bestSpan - norm.mean) / norm.sd)} y="75" textAnchor="middle" fontSize="9" fill="#7c3aed" fontWeight="700">you</text>
                  <text x="8" y="75" textAnchor="start" fontSize="8" fill="#94a3b8">weaker</text>
                  <text x="292" y="75" textAnchor="end" fontSize="8" fill="#94a3b8">stronger</text>
                </svg>
              </div>
            </div>

            {/* THE CTA SITS HERE, not below the analysis.
                It used to be the fifth thing on this screen, after the curve,
                the near miss strip and two bar charts. By the time a reader got
                there the moment had passed. Eyes land on the score, so the
                invitation goes immediately underneath it and everything
                explanatory moves below. */}
            <div className="px-6 sm:px-8 pt-6 pb-2">
              <Link
                href="/test"
                className="group relative block w-full overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-[length:200%_100%] hover:bg-[position:100%_0] p-[3px] shadow-xl shadow-purple-500/25 transition-[background-position] duration-700"
              >
                <span className="block rounded-[13px] bg-white px-6 py-5 text-center transition-colors group-hover:bg-transparent">
                  <span className="block text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent group-hover:text-white transition-colors">
                    Now find your actual IQ
                  </span>
                  <span className="mt-1 flex items-center justify-center text-sm font-medium text-gray-500 group-hover:text-blue-100 transition-colors">
                    50 questions · all four domains · instant score
                    <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </span>
              </Link>
            </div>

            {/* THE TEASE.
                A near miss is the most motivating thing available on this
                screen, so it is stated in the currency the reader already
                cares about: not "you failed at 8" but what 8 would have been
                worth. True, checkable, and it is what makes people retry. */}
            {bestSpan > 0 && bestSpan < MAX_SPAN && (
              <div className="px-6 sm:px-8 py-4 bg-blue-50 border-b border-blue-100 text-center">
                <p className="text-sm text-blue-900 m-0">
                  <strong>One more digit</strong> would have put you at the{' '}
                  <strong className="tabular-nums">{Math.round(Math.min(99, percentileForSpan(bestSpan + 1, mode)))}th</strong>{' '}
                  percentile.
                  {personalBest !== null && !isNewBest && bestSpan < personalBest && (
                    <> Your best is <strong className="tabular-nums">{personalBest}</strong>.</>
                  )}
                </p>
              </div>
            )}

            <div className="px-6 sm:px-8 py-8">
              {/* Supporting detail, below the invitation rather than in front
                  of it. One sentence and one bar instead of the two-chart
                  comparison that used to sit between the score and the CTA. */}
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 mb-5">
                <p className="text-sm text-gray-700 text-center leading-relaxed mb-4">
                  Working memory alone puts you somewhere between{' '}
                  <strong className="tabular-nums">{Math.round(estimate.low)}</strong> and{' '}
                  <strong className="tabular-nums">{Math.round(estimate.high)}</strong>.
                  The full test narrows that to a span of 8 points.
                </p>
                {(() => {
                  const MIN = 55, MAX = 145
                  const pct = (v) => ((Math.max(MIN, Math.min(MAX, v)) - MIN) / (MAX - MIN)) * 100
                  return (
                    <>
                      <div className="relative h-6 rounded-lg bg-white border border-gray-200 overflow-hidden">
                        <div
                          className="absolute inset-y-0 bg-gradient-to-r from-blue-300 via-indigo-400 to-purple-300 opacity-70"
                          style={{ left: `${pct(estimate.low)}%`, width: `${pct(estimate.high) - pct(estimate.low)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[11px] text-gray-400 tabular-nums mt-1">
                        <span>{MIN}</span><span>100</span><span>{MAX}</span>
                      </div>
                    </>
                  )
                })()}
              </div>

              <div className="flex items-center justify-center gap-5 mt-5">
                <button
                  type="button"
                  onClick={start}
                  className="inline-flex items-center text-gray-600 hover:text-gray-900 font-semibold text-sm transition-colors"
                >
                  <RotateCcw className="w-4 h-4 mr-1.5" />
                  Try again
                </button>
                {personalBest !== null && (
                  <span className="text-sm text-gray-400 tabular-nums">Best: {personalBest}</span>
                )}
              </div>

              <p className="text-xs text-gray-400 mt-5 max-w-md mx-auto leading-relaxed text-center">
                One trial per length, so treat this as an indication rather than
                a measurement. A clinical administration gives two trials at each
                length and stops only after both fail. Adult average for{' '}
                {norm.label.toLowerCase()} span is {norm.mean} digits.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* The prompt fires four seconds AFTER the result appears, not during the
          test. Firing it mid-run would drop a modal over someone in the middle
          of memorising a sequence, which is the one moment their attention is
          genuinely committed. Mounting it only on the finished phase means its
          timer starts when the result does, so delayMs is the pause after the
          score lands rather than time on the page.

          Copy is passed in because the defaults talk about an IQ score, and
          here the reader has a digit span. The gap named is the same one the
          result screen names, phrased for someone who has just seen a number
          and does not yet know what the rest of their profile looks like. */}
      {phase === 'finished' && (
        <TestPromptPopup
          delayMs={4000}
          storageKey="biqt_digit_span_prompt"
          headline="That is one domain. Want the whole picture?"
          body={
            <>
              You just measured working memory, one of four domains behind a
              full-scale IQ score. Our 50-question assessment covers verbal,
              numerical and spatial reasoning too, and gives you a score with
              your exact percentile.
            </>
          }
        />
      )}
    </div>
  )
}
